import AppContext from 'App/AppContext';
import { IPPrimaryButton, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connectDocumentConnectionAccount } from './documentConnectionGrant';
import { notifyBrokeredError } from './brokeredErrors';

interface IConnectProviderAccountCardProps {
  connectionId: number;
  /** Named on the button, so it reads "Connect Nextcloud". */
  providerName: string;
  /** The translated DOCPROV0012 sentence, which names the connection. */
  message: string;
  /** Called once the account is connected, so the host re-runs what failed. */
  onConnected: () => void;
}

/**
 * What a user sees instead of a listing, a preview or an upload when the category's connection
 * uses each user's own account and theirs is not connected yet (DOCPROV0012). One button opens
 * the provider's consent page in a popup; when it reports back the host repeats the action.
 */
export const ConnectProviderAccountCard = (props: IConnectProviderAccountCardProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['documentproviders']);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);

  const connect = async () => {
    try {
      setIsConnecting(true);
      const result = await connectDocumentConnectionAccount(appContext, props.connectionId);

      switch (result.outcome) {
        case 'connected':
          appContext.showNotification(
            t('documentproviders:Grant.Connected', { provider: props.providerName }),
            false,
            false,
            true,
          );
          props.onConnected();
          break;
        case 'blocked':
          appContext.showNotification(t('documentproviders:Grant.PopupBlocked'), true, false, true);
          break;
        case 'failed':
          appContext.showNotification(
            t('documentproviders:Grant.Failed', { provider: props.providerName, error: result.error ?? '' }),
            true,
            false,
            true,
          );
          break;
        default:
          // the person closed the popup: nothing to say, the card stays
          break;
      }
    } catch (err) {
      notifyBrokeredError(appContext, err, t);
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <IPStack verticalFill verticalAlign="center" horizontalAlign="center" tokens={globalStackTokensGapSmall}>
      <IPStack.Item styles={{ root: { maxWidth: 480, textAlign: 'center' } }}>
        <IPText block styles={globalTextStylesBold}>
          {t('documentproviders:Grant.Title', { provider: props.providerName })}
        </IPText>
      </IPStack.Item>
      <IPStack.Item styles={{ root: { maxWidth: 480, textAlign: 'center' } }}>
        <IPText block>{props.message}</IPText>
      </IPStack.Item>
      <IPStack.Item styles={{ root: { maxWidth: 480, textAlign: 'center' } }}>
        <IPText block variant="small">
          {t('documentproviders:Grant.Description', { provider: props.providerName })}
        </IPText>
      </IPStack.Item>
      <IPStack.Item>
        <IPPrimaryButton
          text={t('documentproviders:Grant.Connect', { provider: props.providerName })}
          disabled={isConnecting}
          onClick={() => {
            connect();
          }}
        />
      </IPStack.Item>
    </IPStack>
  );
};
